import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { teacherAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

export const fetchTeachers = createAsyncThunk('teachers/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await teacherAPI.fetchTeachers(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch teachers'));
  }
});

export const fetchTeacher = createAsyncThunk('teachers/fetchOne', async (id, { rejectWithValue }) => {
  try {
    const response = await teacherAPI.fetchTeacher(id);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch teacher'));
  }
});

export const createTeacher = createAsyncThunk('teachers/create', async (data, { rejectWithValue }) => {
  try {
    const response = await teacherAPI.createTeacher(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create teacher'));
  }
});

export const updateTeacher = createAsyncThunk('teachers/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await teacherAPI.updateTeacher(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to update teacher'));
  }
});

export const deleteTeacher = createAsyncThunk('teachers/delete', async (id, { rejectWithValue }) => {
  try {
    await teacherAPI.deleteTeacher(id);
    return id;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to delete teacher'));
  }
});

export const assignSubject = createAsyncThunk('teachers/assignSubject', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await teacherAPI.assignSubject(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to assign subject'));
  }
});

export const assignClass = createAsyncThunk('teachers/assignClass', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await teacherAPI.assignClass(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to assign class'));
  }
});

const initialState = {
  teachers: [],
  currentTeacher: null,
  total: 0,
  page: 1,
  limit: 10,
  loading: false,
  error: null,
};

const teacherSlice = createSlice({
  name: 'teachers',
  initialState,
  reducers: {
    clearTeacherError: (state) => { state.error = null; },
    setCurrentTeacher: (state, action) => { state.currentTeacher = action.payload; },
    clearCurrentTeacher: (state) => { state.currentTeacher = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeachers.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchTeachers.fulfilled, (state, action) => {
        state.loading = false;
        state.teachers = action.payload.data || action.payload;
        state.total = action.payload.total || 0;
        state.page = action.payload.page || 1;
        state.limit = action.payload.limit || 10;
      })
      .addCase(fetchTeachers.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(fetchTeacher.pending, (state) => { state.loading = true; })
      .addCase(fetchTeacher.fulfilled, (state, action) => { state.loading = false; state.currentTeacher = action.payload; })
      .addCase(fetchTeacher.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createTeacher.pending, (state) => { state.loading = true; })
      .addCase(createTeacher.fulfilled, (state, action) => { state.loading = false; state.teachers.unshift(action.payload); })
      .addCase(createTeacher.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(updateTeacher.pending, (state) => { state.loading = true; })
      .addCase(updateTeacher.fulfilled, (state, action) => {
        state.loading = false;
        state.teachers = state.teachers.map((t) => t.id === action.payload.id ? action.payload : t);
        state.currentTeacher = action.payload;
      })
      .addCase(updateTeacher.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(deleteTeacher.fulfilled, (state, action) => {
        state.teachers = state.teachers.filter((t) => t.id !== action.payload);
        if (state.currentTeacher?.id === action.payload) state.currentTeacher = null;
      })
      .addCase(assignSubject.fulfilled, (state, action) => {
        state.currentTeacher = action.payload;
        state.teachers = state.teachers.map((t) => t.id === action.payload.id ? action.payload : t);
      })
      .addCase(assignClass.fulfilled, (state, action) => {
        state.currentTeacher = action.payload;
        state.teachers = state.teachers.map((t) => t.id === action.payload.id ? action.payload : t);
      });
  },
});

export const { clearTeacherError, setCurrentTeacher, clearCurrentTeacher } = teacherSlice.actions;
export default teacherSlice.reducer;
